/**
 * Icon Sets Configuration
 * 
 * Each icon set defines icons for different hotspot categories.
 * Icons can be:
 * - Emoji strings (e.g., '🌳')
 * - Image paths (e.g., require('../assets/icons/park.png'))
 * - Custom components (for future extensibility)
 * 
 * To add a new icon set:
 * 1. Add a new key to ICON_SETS with category mappings
 * 2. Add the key to AVAILABLE_ICON_SETS array
 * 3. (Optional) Add metadata to ICON_SET_META
 * 
 * To add a new hotspot category type:
 * 1. Add the category to the GraphQL schema enum (amplify/backend/api/chessmaps/schema.graphql)
 * 2. Add the category to HOTSPOT_CATEGORIES below
 * 3. Add icon mappings for the new category to ALL icon sets (DEFAULT_ICONS, MINIMAL_ICONS, etc.)
 * 4. Add label mapping in src/components/HotspotPopup.js getCategoryLabel() function
 * 5. Run: amplify push (to deploy schema changes)
 */

// Hotspot categories that need icons
export const HOTSPOT_CATEGORIES = {
  PARK: 'PARK',
  CAFE: 'CAFE',
  BAR: 'BAR',
  CLUB: 'CLUB',
  UNIVERSITY: 'UNIVERSITY',
  PUBLIC_SQUARE: 'PUBLIC_SQUARE',
  LIBRARY: 'LIBRARY',
  OTHER: 'OTHER',
};

// Default icon set - the current emojis being used
const DEFAULT_ICONS = {
  PARK: '🌳',
  CAFE: '☕',
  BAR: '🍺',
  CLUB: '♟️',
  UNIVERSITY: '🎓',
  PUBLIC_SQUARE: '📍',
  LIBRARY: '📚',
  OTHER: '♟️',
  // Fallback for unknown categories
  FALLBACK: '📍',
};

// Minimal icon set - cleaner, simpler icons
const MINIMAL_ICONS = {
  PARK: '🌿',
  CAFE: '☕',
  BAR: '🍷',
  CLUB: '♞',
  UNIVERSITY: '📚',
  PUBLIC_SQUARE: '◉',
  LIBRARY: '📖',
  OTHER: '♜',
  FALLBACK: '◉',
};

// Placeholder for custom icon set - will use image assets later
// For now, using alternative emojis as placeholders
const CUSTOM_ICONS = {
  PARK: '🏞️',
  CAFE: '🫖',
  BAR: '🥂',
  CLUB: '♛',
  UNIVERSITY: '🏛️',
  PUBLIC_SQUARE: '⭐',
  LIBRARY: '📕',
  OTHER: '🎯',
  FALLBACK: '⭐',
};

// Retro arcade theme
const RETRO_ICONS = {
  PARK: '🎮',
  CAFE: '👾',
  BAR: '🕹️',
  CLUB: '🏆',
  UNIVERSITY: '📟',
  PUBLIC_SQUARE: '💎',
  LIBRARY: '📚',
  OTHER: '⚡',
  FALLBACK: '💎',
};

// Nature theme
const NATURE_ICONS = {
  PARK: '🌲',
  CAFE: '🌻',
  BAR: '🍃',
  CLUB: '🦋',
  UNIVERSITY: '🌸',
  PUBLIC_SQUARE: '🌺',
  LIBRARY: '📚',
  OTHER: '🍀',
  FALLBACK: '🌺',
};

// All icon sets
export const ICON_SETS = {
  default: DEFAULT_ICONS,
  minimal: MINIMAL_ICONS,
  custom: CUSTOM_ICONS,
  retro: RETRO_ICONS,
  nature: NATURE_ICONS,
};

// Available icon set keys
export const AVAILABLE_ICON_SETS = Object.keys(ICON_SETS);

// Icon set metadata for UI display
export const ICON_SET_META = {
  default: {
    name: 'Default',
    description: 'Classic emoji icons',
    preview: ['🌳', '☕', '🍺', '♟️'],
  },
  minimal: {
    name: 'Minimal',
    description: 'Clean and simple',
    preview: ['🌿', '☕', '🍷', '♞'],
  },
  custom: {
    name: 'Custom',
    description: 'Upload your own icons',
    preview: ['🏞️', '🫖', '🥂', '♛'],
  },
  retro: {
    name: 'Retro',
    description: 'Arcade game vibes',
    preview: ['🎮', '👾', '🕹️', '🏆'],
  },
  nature: {
    name: 'Nature',
    description: 'Natural elements',
    preview: ['🌲', '🌻', '🍃', '🦋'],
  },
};

// Default icon set key
export const DEFAULT_ICON_SET = 'default';

/**
 * Get icon for a category from a specific icon set
 * @param {string} category - The hotspot category
 * @param {string} iconSetKey - The icon set key (default: 'default')
 * @returns {string} The icon for the category
 */
export const getIconForCategory = (category, iconSetKey = DEFAULT_ICON_SET) => {
  const iconSet = ICON_SETS[iconSetKey] || ICON_SETS[DEFAULT_ICON_SET];
  const key = (category && String(category).toUpperCase()) || '';
  return iconSet[key] || iconSet.FALLBACK || DEFAULT_ICONS.FALLBACK;
};

/**
 * Get all icons for an icon set
 * @param {string} iconSetKey - The icon set key
 * @returns {object} The icon set
 */
export const getIconSet = (iconSetKey) => {
  return ICON_SETS[iconSetKey] || ICON_SETS[DEFAULT_ICON_SET];
};

/**
 * Check if an icon set exists
 * @param {string} iconSetKey - The icon set key to check
 * @returns {boolean}
 */
export const isValidIconSet = (iconSetKey) => {
  return AVAILABLE_ICON_SETS.includes(iconSetKey);
};

